// pages/auth/AuthSuccess.jsx - Handles OAuth callback token
import React, { useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { axiosInstance } from "../../config";
import { trackSignupPixels } from "../../utils/adPixels";
import { track } from "../../utils/analytics";

const RETURN_URL_STORAGE_KEY = "cb_post_auth_return";

function popReturnUrl() {
  try {
    const raw = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    if (typeof raw === "string" && raw.startsWith("/") && !raw.startsWith("//")) {
      return raw;
    }
  } catch {}
  return "/home";
}

const AuthSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  useEffect(() => {
    const handleAuth = async () => {
      const token = searchParams.get("token");
      // Take the token out of the address bar right away (history, screenshots, shares).
      if (token) {
        try {
          const clean = new URL(window.location.href);
          clean.searchParams.delete("token");
          window.history.replaceState(window.history.state, "", clean.pathname + clean.search + clean.hash);
        } catch { /* ignore */ }
      }
      const error = searchParams.get("error");

      if (error) {
        console.error("Auth error:", error);
        navigate("/login?error=" + error);
        return;
      }

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        // Store token
        localStorage.setItem("token", token);

        // Fetch user data
        const res = await axiosInstance.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const user = res.data;
        localStorage.setItem("user", JSON.stringify(user));

        // Brand-new account (Google sends &new=1): browser CompleteRegistration
        // for TikTok/Meta, deduped with the server's event.
        if (searchParams.get("new") === "1") {
          trackSignupPixels(user);
          // The marketing site only fires signed_up for email sign-ups.
          track("signed_up", { method: "google" });
        }

        // Set the refresh cookie from our own origin — Google's callback
        // runs on another host where the browser drops it, and the channel
        // connect step in onboarding needs it. Best-effort; _retry keeps a
        // 401 here from triggering the refresh-or-logout interceptor.
        await axiosInstance
          .post("/auth/session", null, { headers: { Authorization: `Bearer ${token}` }, _retry: true })
          .catch(() => {});
        dispatch({ type: "LOGIN_SUCCESS", payload: user });

        navigate(popReturnUrl());
      } catch (err) {
        console.error("Auth success error:", err);
        localStorage.removeItem("token");
        navigate("/login?error=invalid_token");
      }
    };

    handleAuth();
  }, [searchParams, navigate, dispatch]);

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0a0a0a",
      color: "#fff",
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 40,
          height: 40,
          border: "3px solid #333",
          borderTopColor: "#ff6a00",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 20px",
        }} />
        <p style={{ color: "#888" }}>Signing you in...</p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthSuccess;